import { html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
    color: string;
    size: number;
}

interface Rocket {
    x: number;
    y: number;
    vy: number;
    targetY: number;
    color: string;
}

const COLORS = [
    '#FFD700', // gold
    '#FF4444', // red
    '#4488FF', // blue
    '#44FF88', // green
    '#BB66FF', // purple
    '#FFFFFF', // white
    '#FF8844', // orange
    '#FF66AA', // pink
];

const GRAVITY = 0.06;
const PARTICLE_COUNT_MIN = 40;
const PARTICLE_COUNT_MAX = 80;

/**
 * Canvas-based fireworks animation overlay for celebrating high scores.
 * Sits over the alphaTab viewer, same positioning as count-in-overlay.
 */
@customElement('fireworks-overlay')
export class FireworksOverlay extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: none;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 15;
                pointer-events: none;
            }

            :host([active]) {
                display: block;
            }

            canvas {
                width: 100%;
                height: 100%;
            }
        `,
    ];

    @property({ type: Boolean, reflect: true })
    active = false;

    @query('canvas')
    private _canvas!: HTMLCanvasElement;

    private _particles: Particle[] = [];
    private _rockets: Rocket[] = [];
    private _animFrame: number | null = null;
    private _launchInterval: number | null = null;
    private _dismissTimer: number | null = null;

    /**
     * Start the fireworks animation for a given duration.
     */
    start(durationMs: number): void {
        this.stop();
        this.active = true;

        // Size canvas after display:block takes effect
        requestAnimationFrame(() => {
            this._sizeCanvas();
            this._particles = [];
            this._rockets = [];

            // Launch initial burst of rockets
            for (let i = 0; i < 3; i++) {
                setTimeout(() => this._launchRocket(), i * 150);
            }

            // Continue launching rockets periodically
            this._launchInterval = window.setInterval(() => {
                const count = 1 + Math.floor(Math.random() * 2);
                for (let i = 0; i < count; i++) {
                    this._launchRocket();
                }
            }, 600);

            // Start animation loop
            this._animate();

            // Auto-dismiss
            this._dismissTimer = window.setTimeout(() => this.stop(), durationMs);
        });
    }

    /**
     * Stop the animation and clean up.
     */
    stop(): void {
        if (this._animFrame !== null) {
            cancelAnimationFrame(this._animFrame);
            this._animFrame = null;
        }
        if (this._launchInterval !== null) {
            clearInterval(this._launchInterval);
            this._launchInterval = null;
        }
        if (this._dismissTimer !== null) {
            clearTimeout(this._dismissTimer);
            this._dismissTimer = null;
        }
        this._particles = [];
        this._rockets = [];
        this.active = false;
    }

    private _sizeCanvas(): void {
        const canvas = this._canvas;
        if (!canvas) return;
        canvas.width = this.offsetWidth * (window.devicePixelRatio || 1);
        canvas.height = this.offsetHeight * (window.devicePixelRatio || 1);
    }

    private _launchRocket(): void {
        const canvas = this._canvas;
        if (!canvas) return;
        const w = canvas.width;
        const h = canvas.height;

        this._rockets.push({
            x: w * (0.15 + Math.random() * 0.7),
            y: h,
            vy: -(h * 0.012 + Math.random() * h * 0.008),
            targetY: h * (0.1 + Math.random() * 0.35),
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
        });
    }

    private _explodeRocket(rocket: Rocket): void {
        const count = PARTICLE_COUNT_MIN + Math.floor(Math.random() * (PARTICLE_COUNT_MAX - PARTICLE_COUNT_MIN));
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 1 + Math.random() * 4;
            const life = 40 + Math.floor(Math.random() * 40);
            this._particles.push({
                x: rocket.x,
                y: rocket.y,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                life,
                maxLife: life,
                color: Math.random() < 0.3
                    ? COLORS[Math.floor(Math.random() * COLORS.length)]
                    : rocket.color,
                size: 2 + Math.random() * 2,
            });
        }
    }

    private _animate = (): void => {
        if (!this.active) return;

        const canvas = this._canvas;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const w = canvas.width;
        const h = canvas.height;

        // Semi-transparent clear for trail effect
        ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
        ctx.fillRect(0, 0, w, h);

        // Update and draw rockets
        for (let i = this._rockets.length - 1; i >= 0; i--) {
            const r = this._rockets[i];
            r.y += r.vy;

            // Draw rocket trail
            ctx.fillStyle = r.color;
            ctx.fillRect(r.x - 1, r.y, 2, 6);

            // Explode when reaching target height
            if (r.y <= r.targetY) {
                this._explodeRocket(r);
                this._rockets.splice(i, 1);
            }
        }

        // Update and draw particles
        for (let i = this._particles.length - 1; i >= 0; i--) {
            const p = this._particles[i];
            p.x += p.vx;
            p.y += p.vy;
            p.vy += GRAVITY;
            p.life--;

            if (p.life <= 0) {
                this._particles.splice(i, 1);
                continue;
            }

            const alpha = p.life / p.maxLife;
            const size = p.size * alpha;

            ctx.globalAlpha = alpha;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.globalAlpha = 1;

        this._animFrame = requestAnimationFrame(this._animate);
    };

    render() {
        return html`<canvas></canvas>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fireworks-overlay': FireworksOverlay;
    }
}
